import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Options for the useScrollHint hook.
 */
export interface UseScrollHintOptions {
  /**
   * Whether the hook should be active. When false, listeners are not attached
   * and values reset to non-scrollable state.
   */
  enabled?: boolean;
  /**
   * Distance in pixels over which the hint should fade from 1 → 0 opacity.
   * Defaults to 48.
   */
  fadeDistancePx?: number;
  /**
   * Delay before the first measurement, in milliseconds. Useful to avoid
   * measuring during entry animations (e.g., height 0→auto). Defaults to 200.
   */
  initDelayMs?: number;
}

/**
 * Result returned by useScrollHint.
 */
export interface UseScrollHintResult<T extends HTMLElement> {
  /**
   * Ref to attach to the scrollable container element.
   */
  containerRef: React.RefObject<T>;
  /**
   * True once the first stable measurement has been performed.
   */
  isInitialized: boolean;
  /**
   * True if the content overflows vertically.
   */
  isScrollable: boolean;
  /**
   * True if the scroll position is at (or within 1px of) the bottom.
   */
  isAtBottom: boolean;
  /**
   * Opacity for the hint (0..1), based on current scroll position.
   */
  hintOpacity: number;
}

/**
 * useScrollHint centralizes logic to detect vertical overflow, bottom position,
 * and a scroll-progress-driven opacity for a hint affordance.
 *
 * Attach the returned `containerRef` to the scrollable element.
 * @param options - Observation, initial settling delay and hint fade settings.
 * @returns The container ref and current overflow, position and hint state.
 */
export const useScrollHint = <T extends HTMLElement = HTMLDivElement>(
  options?: UseScrollHintOptions
): UseScrollHintResult<T> => {
  const {
    enabled = true,
    fadeDistancePx = 48,
    initDelayMs = 200,
  } = options ?? {};

  const containerRef = useRef<T | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [hintOpacity, setHintOpacity] = useState(0);

  const clamp01 = useMemo(() => (n: number) => Math.max(0, Math.min(1, n)), []);

  useEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el) {
      setIsInitialized(false);
      setIsScrollable(false);
      setIsAtBottom(false);
      setHintOpacity(0);
      return;
    }

    const computeOpacity = () =>
      clamp01(1 - Math.min(el.scrollTop, fadeDistancePx) / fadeDistancePx);

    const computeIsAtBottom = () =>
      el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

    const computeIsScrollable = () => el.scrollHeight > el.clientHeight + 1;

    const recomputeAll = () => {
      const hasVertical = computeIsScrollable();
      const atBottom = computeIsAtBottom();
      setIsScrollable(hasVertical);
      setIsAtBottom(atBottom);
      setHintOpacity(hasVertical && !atBottom ? computeOpacity() : 0);
    };

    const handleResize = () => recomputeAll();

    const handleScroll = () => {
      const atBottom = computeIsAtBottom();
      setIsAtBottom(atBottom);
      setHintOpacity(atBottom ? 0 : computeOpacity());
    };

    let resizeObserver: ResizeObserver | undefined;
    const isResizeObserverSupported = typeof ResizeObserver !== 'undefined';

    if (isResizeObserverSupported) {
      resizeObserver = new ResizeObserver(recomputeAll);
      resizeObserver.observe(el);
      // Content can grow (for example with text zoom) inside a fixed-size panel.
      const content = el.firstElementChild;
      if (content) resizeObserver.observe(content);
    }

    const scrollOptions = { passive: true };
    window.addEventListener('resize', handleResize);
    el.addEventListener('scroll', handleScroll, scrollOptions);

    const initTimer = window.setTimeout(() => {
      requestAnimationFrame(() => {
        recomputeAll();
        setIsInitialized(true);
      });
    }, initDelayMs);

    return () => {
      window.removeEventListener('resize', handleResize);
      el.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      window.clearTimeout(initTimer);
    };
  }, [enabled, fadeDistancePx, initDelayMs, clamp01]);

  return { containerRef, isInitialized, isScrollable, isAtBottom, hintOpacity };
};
